import React, { Dispatch, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AdditionalInputTypeEnum } from '../../../../redux/project/sliceProjectWorkflow';
import { IAppReduxStore } from '../../../../redux/store';
import {
    FormControl,
    FormGroup,
    FormLabel,
    IconButton,
    List,
    ListItem,
    ListItemText,
    SxProps,
    TextField,
} from '@mui/material';
import { headingFontBold, bodyFont, vars } from '../../../../Theme';
import { useTranslation } from 'react-i18next';
import { mdiClose, mdiAlertCircle, mdiInformation, mdiOpenInNew } from '@mdi/js';
import Message from './Message';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Icon from '@mdi/react';
import dayjs, { Dayjs } from 'dayjs';
import CheckboxTree from './CheckboxTree';
import UploadFile from './UploadFile';
import {
    appendFiles,
    getMappedFilesToRelatedDocuments,
    removeFile,
} from '../../../../redux/import/sliceImportFileUpload';
import RadioButtonGroupSearch from './RadioButtonGroupSearch';
import SelectViewCategoryListDialogContent from '../SelectViewCategoryList/SelectViewCategoryListDialogContent';
import PasswordField from './PasswordField';
import DigiSealSignDialogContent from './DigiSealSignDialogContent';

/** Styles for FormLabel. */
const formLabelSx: SxProps = {
    ...headingFontBold,
    fontSize: '16px',
    color: vars.black,
    fontWeight: 700,
    marginBottom: '8px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

const textFieldSx: SxProps = {
    input: { ...bodyFont, p: 0 },

    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            border: `2px solid ${vars.mainGreen}`,
        },
    },
};

const listItemSx: SxProps = {
    '&:hover': {
        backgroundColor: `${vars.lightGray}`,
    },
};

const ErrorMessage = ({ text }: { text: string }) => {
    return (
        <Message
            text={text}
            icon={mdiAlertCircle}
            color={vars.errorDialogRed}
            typographySx={{ fontWeight: 700 }}
            wrapperSx={{
                border: 'none',
                padding: '2px',
                mb: 1,
            }}
        />
    );
};

export default ({
    additionalInputState,
    onAdditionalInputStateChanged,
    hasAdditionalInputError,
    setAdditionalInputErrorState,
    type,
}: IAdditionalInputContentProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'workflowAdditionalInput' });

    const { dialogOptionChoice, dialogInformation, dialogPrompt, dialogType } = useSelector(
        (_store: IAppReduxStore) =>
            type === IAdditionalInputTypeEnum.Workflow
                ? _store.projectWorkflow.additionalInput?.response
                : _store.projectDocumentsDynamicSelection.additionalInput?.response,
    );

    const documents = useSelector(getMappedFilesToRelatedDocuments);

    useEffect(() => {
        if (dialogType === AdditionalInputTypeEnum.DigiSealSign && dialogOptionChoice.length > 0) {
            const digiSignUrl = dialogOptionChoice.find(item => item.alias === 'url').value;

            window.open(digiSignUrl, '_blank');
        }
    }, [dialogOptionChoice]);

    useEffect(() => {
        if (documents.length > 0) {
            setAdditionalInputErrorState(false);
        }
    }, [documents]);

    const handleChangeCheckbox = (value: string, checked: boolean) => {
        onAdditionalInputStateChanged(
            additionalInputState.filter(opt => opt !== value).concat(checked ? value : []),
        );
        setAdditionalInputErrorState(false);
    };

    const handleChangeInput = (value: string) => {
        onAdditionalInputStateChanged(value ? [value] : []);
        setAdditionalInputErrorState(false);
    };

    const multipleChoices = useMemo(() => {
        return dialogOptionChoice.map(x => ({
            id: x.alias,
            path: x.alias,
            label: x.value,
            parent: '',
        }));
    }, [dialogOptionChoice]);

    const inputStateValue: string = additionalInputState[0] || '';

    const additionalInputTypes = {
        [AdditionalInputTypeEnum.ShowText]: <></>,
        [AdditionalInputTypeEnum.ShowUrlList]: (
            <List>
                {dialogOptionChoice.map((link, index) => {
                    return (
                        <ListItem
                            key={`${link.value} - ${index}`}
                            sx={listItemSx}
                            secondaryAction={
                                <IconButton
                                    onClick={() => window.open(link.alias, '_blank')}
                                    aria-label='open in new tab'
                                    edge='end'
                                >
                                    <Icon path={mdiOpenInNew} size={1} />
                                </IconButton>
                            }
                        >
                            <ListItemText primary={link.value} />
                        </ListItem>
                    );
                })}
            </List>
        ),
        [AdditionalInputTypeEnum.InputDate]: (
            <>
                {hasAdditionalInputError && <ErrorMessage text={t('errorEnterDate')} />}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        value={inputStateValue ? dayjs(inputStateValue, 'DD.MM.YYYY') : null}
                        onChange={(value: Dayjs) =>
                            handleChangeInput(value?.isValid() ? value?.format('DD.MM.YYYY') : null)
                        }
                        onError={() => setAdditionalInputErrorState(false)}
                        slotProps={{
                            textField: {
                                error: hasAdditionalInputError,
                                InputProps: {
                                    startAdornment: (
                                        <IconButton
                                            onClick={() => handleChangeInput(null)}
                                            disabled={!inputStateValue}
                                            style={{ order: 1 }}
                                        >
                                            <Icon path={mdiClose} size={1} />
                                        </IconButton>
                                    ),
                                    size: 'small',
                                },
                            },
                            inputAdornment: {
                                position: 'end',
                                style: { order: 2, marginLeft: 0 },
                            },
                        }}
                        format='DD.MM.YYYY'
                    />
                </LocalizationProvider>
            </>
        ),
        [AdditionalInputTypeEnum.InputText]: (
            <>
                {hasAdditionalInputError && <ErrorMessage text={t('errorEnterText')} />}
                <TextField
                    size='small'
                    sx={textFieldSx}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeInput(e.target.value)
                    }
                    placeholder={t('enterText')}
                    value={inputStateValue}
                    multiline
                    error={hasAdditionalInputError}
                />
            </>
        ),

        [AdditionalInputTypeEnum.ChoiceMultiple]: (
            <>
                {hasAdditionalInputError && <ErrorMessage text={t('errorMakeSelection')} />}
                <FormGroup>
                    <CheckboxTree
                        withSearch={true}
                        items={multipleChoices}
                        checkedNodes={additionalInputState}
                        halfCheckedNodes={[]}
                        onCheckNode={handleChangeCheckbox}
                        hasError={hasAdditionalInputError}
                    />
                </FormGroup>
            </>
        ),
        [AdditionalInputTypeEnum.ChoiceExclusive]: (
            <>
                {hasAdditionalInputError && <ErrorMessage text={t('errorMakeSelection')} />}
                <RadioButtonGroupSearch
                    options={dialogOptionChoice.map(option => ({
                        key: option.alias,
                        label: option.value,
                    }))}
                    value={inputStateValue}
                    onChange={handleChangeInput}
                    hasError={hasAdditionalInputError}
                />
            </>
        ),
        [AdditionalInputTypeEnum.UploadFiles]: (
            <>
                {hasAdditionalInputError && <ErrorMessage text={t('errorMakeSelection')} />}
                <FormControl>
                    <UploadFile
                        buttonLabel={t('addFiles')}
                        documentsLabel={t('filesOverview', { count: documents.length })}
                        acceptFileTypes='.pdf,.dot,.doc,.docx'
                        appendFilesInfo={appendFiles}
                        removeFile={removeFile}
                        uploadedDocuments={documents}
                        allowMultiple={true}
                    />
                </FormControl>
            </>
        ),
        [AdditionalInputTypeEnum.SelectViewCategoryList]: (
            <>
                {hasAdditionalInputError && <ErrorMessage text={t('errorMakeSelection')} />}
                <FormControl>
                    <SelectViewCategoryListDialogContent
                        itemAliasValues={dialogOptionChoice}
                        additionalInputState={additionalInputState}
                        hasAdditionalInputError={hasAdditionalInputError}
                        onAdditionalInputStateChanged={onAdditionalInputStateChanged}
                    />
                </FormControl>
            </>
        ),
        [AdditionalInputTypeEnum.InputPassword]: (
            <>
                {hasAdditionalInputError && <ErrorMessage text={t('errorEnterText')} />}
                <PasswordField
                    handleChangeInput={handleChangeInput}
                    password={inputStateValue}
                    error={hasAdditionalInputError}
                    placeholder={t('enterPassword')}
                />
            </>
        ),
        [AdditionalInputTypeEnum.DigiSealSign]: (
            <>
                {hasAdditionalInputError && <ErrorMessage text={t('errorMakeSelection')} />}
                <FormControl>
                    <DigiSealSignDialogContent values={dialogOptionChoice} />
                </FormControl>
            </>
        ),
    };

    return (
        <>
            {dialogInformation && <Message text={dialogInformation} icon={mdiInformation} />}
            <FormControl>
                {dialogPrompt && <FormLabel sx={formLabelSx}>{dialogPrompt}</FormLabel>}
                {additionalInputTypes[dialogType]}
            </FormControl>
        </>
    );
};

export enum IAdditionalInputTypeEnum {
    Workflow = 'workflow',
    Generic = 'generic',
}

interface IAdditionalInputContentProps {
    additionalInputState: string[];
    onAdditionalInputStateChanged: (newState: string[]) => void;
    hasAdditionalInputError: boolean;
    setAdditionalInputErrorState: Dispatch<React.SetStateAction<boolean>>;
    type: IAdditionalInputTypeEnum;
}
